/* eslint-disable id-length */
import { UX2 } from '@wsb/guac-widget-core';
import * as utils from '../utils';
import { LIVID_COLOR, WHITE_COLOR, BLACK_COLOR } from '../constants/colors';
import { shadeHex } from './oklab';

const { Color } = UX2.utils;

// Derived shade token handler for `backgroundColor: 'primary100'`.
// Routes through colorDials → shadesDial → returns the derived hex.
// Safe-wrapped: returns null if shadesDial isn't reachable, so the style key
// just gets omitted rather than throwing and breaking the render.
function backgroundColorPrimary100() {
  try {
    return utils.getDial(this, 'primary100');
  } catch (err) {
    return null;
  }
}

// Derived shade token handler for `backgroundColor: 'primary300'` — a lighter
// shade of primary (e.g. a moderate, ~2-step distinct top/promo bar above a
// dark primary nav). Pair with `color: 'primary300On'` for accessible text.
function backgroundColorPrimary300() {
  try {
    return utils.getDial(this, 'primary300');
  } catch (err) {
    return null;
  }
}

// Derived shade token handler for `backgroundColor: 'primary700'` — a darker
// shade of primary (e.g. for a distinct top/promo bar above a primary nav).
// Pair with `color: 'primary700On'` for accessible text.
function backgroundColorPrimary700() {
  try {
    return utils.getDial(this, 'primary700');
  } catch (err) {
    return null;
  }
}

// Derived shade token handler for `backgroundColor: 'primary900'` — the
// darkest primary shade, for maximum standout (e.g. a top/promo bar that needs
// strong separation from a primary nav). Pair with `color: 'primary900On'`.
function backgroundColorPrimary900() {
  try {
    return utils.getDial(this, 'primary900');
  } catch (err) {
    return null;
  }
}

// "Card surface" token: subtle elevation derived from the section background.
// On dark sections, the card is slightly lighter (raised). On light sections,
// the card is slightly darker (recessed). Either way, the card stays in the
// same hue family as the section, so existing text colors (highContrast,
// lowContrast — designed against section bg) remain AA-readable on it.
//
// Use as `backgroundColor: 'cardSurface'` in any theme that wants the
// "subtle elevation" pattern (Stripe / Linear / Vercel-style minimalist themes).
// For brand-tinted cards instead (Notion / Spotify / Slack-style), use
// `backgroundColor: 'primary100'` and pair with `color: 'primary100On'`.
// Smart cardSurface:
//   • If the section background is neutral (HSLuv saturation < 10 — i.e.
//     white / black / gray / cream), tint the card with a shade of the
//     PRIMARY color whose lightness matches the section bg (with a small
//     ± nudge for elevation). Keeps text contrast identical to the
//     section bg, so existing AA-passing text stays readable while the
//     section gets a subtle branded card surface.
//   • If the section bg has any meaningful saturation (a colorful paint
//     job), fall back to the simple "nudge bg" elevation pattern.
function backgroundColorCardSurface() {
  try {
    const bg = utils.getDial(this, 'background');
    const NEUTRAL_SATURATION_THRESHOLD = 10;
    const ELEVATION_NUDGE_LIGHT = 7;
    const ELEVATION_NUDGE_DARK = 8;
    const isNeutralBg = bg.saturation < NEUTRAL_SATURATION_THRESHOLD;

    let result;
    if (isNeutralBg) {
      const primary = utils.getDial(this, 'primary');
      // Clamp to [5, 95] so extreme bg lightness (near-black or
      // near-white neutral) doesn't produce out-of-range setLightness
      // input. Mirrors the avatar handler's clamps below.
      const targetLightness = bg.isLight()
        ? Math.max(5, bg.lightness - ELEVATION_NUDGE_LIGHT)
        : Math.min(95, bg.lightness + ELEVATION_NUDGE_DARK);
      // OKLab shade: same hue + chroma at the new lightness (see ./oklab) —
      // keeps a muted brand muted and avoids HSLuv's purple→pink / teal→wash
      // drift. primary.toHex() → faithful tint → back to a Color for resolution.
      result = Color.fromHex(shadeHex(primary.toHex(), targetLightness));
    } else {
      result = bg.isLight() ? bg.darken(ELEVATION_NUDGE_LIGHT) : bg.lighten(ELEVATION_NUDGE_DARK);
    }

    return result;
  } catch (err) {
    return null;
  }
}

// Reviewer avatar bg — primary-tinted shade more contrasted than
// cardSurface (further from bg luminance) so avatars stand out from
// the card. Currently runs for all themes — scoping to Aurora can be
// added once we confirm the resolution path works.
function backgroundColorReviewerAvatarBg() {
  try {
    const bg = utils.getDial(this, 'background');
    const NEUTRAL_SATURATION_THRESHOLD = 10;
    const AVATAR_CONTRAST_LIGHT = 35;
    const AVATAR_CONTRAST_DARK = 35;
    const isNeutralBg = bg.saturation < NEUTRAL_SATURATION_THRESHOLD;

    let result;
    if (isNeutralBg) {
      const primary = utils.getDial(this, 'primary');
      const targetLightness = bg.isLight()
        ? Math.max(5, bg.lightness - AVATAR_CONTRAST_LIGHT)
        : Math.min(95, bg.lightness + AVATAR_CONTRAST_DARK);
      result = primary.setLightness(targetLightness);
    } else {
      result = bg.isLight() ? bg.darken(AVATAR_CONTRAST_LIGHT) : bg.lighten(AVATAR_CONTRAST_DARK);
    }
    return result;
  } catch (err) {
    return null;
  }
}

// Reviewer initial color — matches cardSurface so the letter sits at
// the card's elevation tint (visually distinct from the avatar bg
// which is more contrasted).
function backgroundColorReviewerInitialColor() {
  try {
    return backgroundColorCardSurface.call(this);
  } catch (err) {
    return null;
  }
}

function backgroundColorSection() {
  return utils.getDial(this, 'background');
}

function backgroundColorSectionOverlay() {
  const background = utils.getDial(this, 'background');
  const newAlpha = background.alpha === 100 ? 90 : background.alpha === 90 ? 50 : 30;
  return background.setAlpha(newAlpha);
}

function backgroundColorSectionOverlaySoft() {
  const background = utils.getDial(this, 'background');
  return background.setAlpha(25);
}

function backgroundColorNeutral() {
  return utils.getDial(this, 'neutral');
}

function backgroundColorAccent() {
  return utils.getDial(this, 'accent');
}

function backgroundColorPrimary() {
  return utils.getDial(this, 'primary');
}

function backgroundColorPrimaryLight() {
  return utils.getDial(this, 'primary').setLightness(96).setAlpha(100);
}

function backgroundColorNeutralOverlay() {
  const [neutral, background] = utils.getDial(this, ['neutral', 'background']);
  const newAlpha = background.alpha === 100 ? 90 : background.alpha === 90 ? 50 : 30;
  return neutral.setAlpha(newAlpha);
}

function backgroundColorAccentOverlay() {
  const [background] = utils.getDial(this, ['background']);
  const newAlpha = background.alpha === 100 ? 90 : background.alpha === 90 ? 50 : 30;
  return BLACK_COLOR.setAlpha(newAlpha);
}

function backgroundColorPrimaryOverlay() {
  const [primary, background] = utils.getDial(this, ['primary', 'background']);
  const newAlpha = background.alpha === 100 ? 90 : background.alpha === 90 ? 50 : 30;
  return primary.setAlpha(newAlpha);
}

function backgroundColorModalOverlay() {
  return BLACK_COLOR.setAlpha(60);
}

function backgroundColorAction() {
  const [highlight, background, multiplier] = utils.getDial(this, [
    'highlight',
    'background',
    'multiplier'
  ]);
  return highlight.contrastWith(background, 10 * multiplier);
}

function backgroundColorActionHover() {
  const [background, multiplier] = utils.getDial(this, ['background', 'multiplier']);
  const colorAction = utils.getBackgroundColor(this, 'action');
  return (
    colorAction.lightness > 10 ? colorAction.lighten(10) : colorAction.darken(10)
  ).contrastWith(background, 15 * multiplier);
}

function backgroundColorActionActive() {
  const colorAction = utils.getBackgroundColor(this, 'action');
  return colorAction.lightness > 10 ? colorAction.darken(15) : colorAction.lighten(15);
}

function backgroundColorActionDisabled() {
  return utils.getBackgroundColor(this, 'action').setAlpha(70);
}

function backgroundColorActionContrast() {
  const [background, multiplier] = utils.getDial(this, ['background', 'multiplier']);
  return background.setAlpha(100).contrast(100 * multiplier);
}

function backgroundColorActionContrastHover() {
  const [background, multiplier] = utils.getDial(this, ['background', 'multiplier']);
  return background.setAlpha(100).contrast(85 * multiplier);
}

function backgroundColorActionContrastActive() {
  return utils.getBackgroundColor(this, 'actionContrast');
}

function backgroundColorActionContrastDisabled() {
  return utils.getBackgroundColor(this, 'actionContrast').setAlpha(70);
}

function backgroundColorInput() {
  const [outline, background] = utils.getDial(this, ['outline', 'background']);
  return outline ? background.setAlpha(100).setLightness(100) : background.setAlpha(0);
}

function backgroundColorInputHover() {
  return utils.getBackgroundColor(this, 'input');
}

function backgroundColorInputActive() {
  return utils.getBackgroundColor(this, 'input');
}

function backgroundColorInputDisabled() {
  return utils.getBackgroundColor(this, 'input').setAlpha(70);
}

function backgroundColorSectionContrast() {
  const [background, multiplier] = utils.getDial(this, ['background', 'multiplier']);
  return background.setAlpha(100).contrast(90 * multiplier);
}

function backgroundColorSectionLowContrast() {
  const [background, multiplier] = utils.getDial(this, ['background', 'multiplier']);
  return background.setAlpha(100).contrast(50 * multiplier);
}

function backgroundColorSectionUltraLowContrast() {
  const [background, multiplier] = utils.getDial(this, ['background', 'multiplier']);
  return background.setAlpha(100).contrast(15 * multiplier);
}

function backgroundColorNavSolid() {
  // currently only used for NavBar background opacity
  return utils.getDial(this, 'background').setAlpha(100);
}

function backgroundColorSearchMobileDark() {
  return new Color('rgb(255, 255, 255)').setAlpha(15);
}

function backgroundColorSearchMobileGray() {
  return new Color('rgb(0, 0, 0)').setAlpha(8);
}

function backgroundColorSearchMobileLight() {
  return new Color('rgb(245, 245, 245)').setAlpha(100);
}

function backgroundColorBrandLivid() {
  const background = utils.getDial(this, 'background');
  const { alpha, lightness } = background;
  const alphaLightIndex = lightness + (100 - alpha);
  return alphaLightIndex >= 85 || alpha === 0 || lightness === 100 ? LIVID_COLOR : WHITE_COLOR;
}

function backgroundColorBrandLividHover() {
  const colorLivid = utils.getBackgroundColor(this, 'brandLivid');
  return colorLivid.lightness > 50 ? colorLivid.darken(5) : colorLivid.lighten(5);
}

export {
  // Color
  backgroundColorSection,
  backgroundColorSectionOverlay,
  backgroundColorSectionOverlaySoft,
  backgroundColorSectionContrast,
  backgroundColorSectionLowContrast,
  backgroundColorSectionUltraLowContrast,
  backgroundColorNeutral,
  backgroundColorNeutralOverlay,
  backgroundColorAccentOverlay,
  backgroundColorPrimary,
  backgroundColorPrimaryLight,
  backgroundColorPrimaryOverlay,
  backgroundColorModalOverlay,
  backgroundColorAccent,
  // Action
  backgroundColorAction,
  backgroundColorActionHover,
  backgroundColorActionActive,
  backgroundColorActionDisabled,
  backgroundColorActionContrast,
  backgroundColorActionContrastHover,
  backgroundColorActionContrastActive,
  backgroundColorActionContrastDisabled,
  // Input
  backgroundColorInput,
  backgroundColorInputHover,
  backgroundColorInputActive,
  backgroundColorInputDisabled,
  // Nav
  backgroundColorNavSolid,
  // Search Mobile
  backgroundColorSearchMobileDark,
  backgroundColorSearchMobileGray,
  backgroundColorSearchMobileLight,
  // Brand
  backgroundColorBrandLivid,
  backgroundColorBrandLividHover,
  // Derived shade tokens (M2; safe-wrapped — return null if shadesDial missing)
  backgroundColorPrimary100,
  backgroundColorPrimary300,
  backgroundColorPrimary700,
  backgroundColorPrimary900,
  // Card surface — subtle elevation of section bg (Approach B; preferred for v1)
  backgroundColorCardSurface,
  // Reviewer avatar bg + initial color — Aurora-scoped (other themes get
  // legacy gray hex); avatar bg is more-contrasted primary-tinted shade,
  // initial color matches cardSurface so the letter contrasts against
  // the avatar bg naturally.
  backgroundColorReviewerAvatarBg,
  backgroundColorReviewerInitialColor
};
